import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Inbox, Copy, Trash2, PenLine } from "lucide-react";
import { Badge } from "../ui/badge";
import { fmtDate } from "./leadUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = {
  Draft: "bg-zinc-600/20 text-zinc-400 border-zinc-500/40",
  Sent: "bg-blue-600/20 text-blue-400 border-blue-500/40",
  Approved: "bg-green-600/20 text-green-400 border-green-500/40",
  Declined: "bg-red-600/20 text-red-400 border-red-500/40",
};

export const EstimatesTab = ({ onAuthError }) => {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/estimates`);
      setEstimates(res.data);
    } catch (err) {
      if (!onAuthError(err)) toast.error("Failed to load estimates");
    } finally {
      setLoading(false);
    }
  }, [onAuthError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const copyLink = async (e) => {
    const link = `${window.location.origin}/estimate/${e.token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Estimate link copied");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) toast.success("Estimate link copied");
      else toast.info(`Copy manually: ${link}`);
    }
  };

  const send = async (e) => {
    try {
      await axios.post(`${API}/estimates/${e.estimate_id}/send`);
      toast.success("Marked as Sent — copy the link and text it to the customer");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete estimate for ${e.name} ($${Number(e.total).toFixed(2)})?`)) return;
    try {
      await axios.delete(`${API}/estimates/${e.estimate_id}`);
      toast.success("Estimate deleted");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  if (estimates.length === 0) {
    return <div data-testid="estimates-empty-state" className="text-center py-24 text-zinc-500"><Inbox className="h-12 w-12 mx-auto mb-4 text-zinc-700" />No estimates yet. Techs create them from job details.</div>;
  }

  return (
    <div className="space-y-3" data-testid="estimates-tab">
      {estimates.map((e) => (
        <div key={e.estimate_id} data-testid={`estimate-row-${e.estimate_id}`} className="bg-[#121212] border border-zinc-800 rounded-xl p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
          <div className="lg:col-span-4">
            <div className="font-heading font-semibold text-white">{e.name} — ${Number(e.total).toFixed(2)}</div>
            <div className="text-zinc-500 text-xs">{e.vehicle || ""} · v{e.version} · by {e.created_by} · {fmtDate(e.created_at)}</div>
            <div className="text-zinc-600 text-xs">{(e.line_items || []).map((i) => i.description).join(", ")}</div>
          </div>
          <div className="lg:col-span-4 text-xs">
            {e.status === "Approved" && e.signature && (
              <div className="text-green-400 inline-flex items-center gap-1"><PenLine className="h-3.5 w-3.5" /> Signed by {e.signature.printed_name} on {fmtDate(e.signature.signed_at)}</div>
            )}
            {e.status === "Declined" && <div className="text-red-400">Declined{e.decline_note ? `: "${e.decline_note}"` : ""}</div>}
            {e.status === "Sent" && <div className="text-blue-400">Awaiting customer signature</div>}
            {e.status === "Draft" && <div className="text-zinc-500">Draft — not yet sent</div>}
          </div>
          <div className="lg:col-span-4 flex items-center gap-2 flex-wrap lg:justify-end">
            <Badge variant="outline" className={`${statusColor[e.status] || statusColor.Draft} rounded-sm`}>{e.status}</Badge>
            {e.status === "Draft" && (
              <button data-testid={`estimate-send-${e.estimate_id}`} onClick={() => send(e)} className="border border-blue-500/50 text-blue-400 rounded-sm px-3 h-9 text-xs font-semibold hover:bg-blue-500/10">Mark Sent</button>
            )}
            <button data-testid={`estimate-copy-${e.estimate_id}`} onClick={() => copyLink(e)} className="p-2 border border-zinc-700 rounded-sm text-zinc-400 hover:text-white" title="Copy customer link"><Copy className="h-4 w-4" /></button>
            <button data-testid={`estimate-delete-${e.estimate_id}`} onClick={() => remove(e)} className="p-2 border border-zinc-800 rounded-sm text-zinc-500 hover:text-red-400" title="Delete"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
};
